import { AsyncSelect } from "@src/components/elements/Select/AsyncSelect";
import { SelectOption } from "@src/components/elements/Select/SelectOption";
import { sudoziFetch } from "@src/infrastructure/apiCall";
import { useSudoziQuery } from "@src/infrastructure/useSudoziQuery";
import { GetMemberSelectOptionsRoute } from "@src/sudozi-schemas/api-route-schemas";
import _ from "lodash";
import React, { useState } from "react";

type Props = {
  label: string;
  disableError?: boolean;
  clearable?: boolean;
  placeholder?: string;
  value: SelectOption | string | null;
  onChange: (value: SelectOption | null) => void;
  menuPortalTarget?: HTMLElement;
  required?: boolean;
  error?: string;
  isSigner?: boolean;
  className?: string;
  inGroupIds?: string[];
  additionalOptions?: SelectOption[];
  labelType?: Parameters<typeof AsyncSelect>[0]["labelType"];
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  tooltipText?: string;
  maxWidth?: string;
  approverForDepartmentIds?: string[];
};

export const AsyncMemberSelect: React.FC<Props> = ({
  label,
  value,
  onChange,
  placeholder,
  clearable,
  disableError,
  menuPortalTarget,
  isSigner,
  error,
  required,
  className,
  inGroupIds,
  additionalOptions,
  labelType,
  onMenuClose,
  onMenuOpen,
  tooltipText,
  maxWidth,
  approverForDepartmentIds,
}) => {
  const { data: defaultOptions } = useSudoziQuery({
    route: GetMemberSelectOptionsRoute,
    query: {
      search: null,
      isSigner: isSigner !== undefined ? `${isSigner}` : undefined,
      inGroupIds,
      approverForDepartmentIds,
    },
    queryKey: [
      GetMemberSelectOptionsRoute.path,
      {
        search: null,
        isSigner: isSigner !== undefined ? `${isSigner}` : undefined,
        inGroupIds,
        approverForDepartmentIds,
      },
    ],
  });

  async function loadOptions(search: string | null) {
    return await sudoziFetch({
      route: GetMemberSelectOptionsRoute,
      query: {
        search,
        isSigner: isSigner !== undefined ? `${isSigner}` : undefined,
        inGroupIds,
        approverForDepartmentIds,
      },
    });
  }

  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
    typeof value === "string" ? { value, label: value } : value,
  );

  const options = selectedOption
    ? _.uniqBy(
        [
          ...(defaultOptions ?? []),
          selectedOption,
          ...(additionalOptions ?? []),
        ],
        "value",
      )
    : [...(defaultOptions ?? []), ...(additionalOptions ?? [])];
  const internalValue =
    typeof value === "string" ? options.find((o) => o.value === value) : value;

  return defaultOptions ? (
    <AsyncSelect
      className={`${error ? "field-error" : ""} ${className ?? ""}`}
      required={required}
      error={error}
      minWidth="100%"
      isClearable={clearable}
      defaultOptions={options}
      disableError={disableError}
      labelType={labelType}
      label={label}
      value={internalValue ?? null}
      loadOptions={loadOptions}
      placeholder={placeholder ?? "Search for a member"}
      onChange={(value) => {
        setSelectedOption(value);
        onChange(value);
      }}
      menuPortalTarget={menuPortalTarget}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      tooltipText={tooltipText}
      maxWidth={maxWidth}
    />
  ) : (
    <div>Loading...</div>
  );
};
